import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, input, output, signal, viewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { APP_CONFIG, BoneViewEnum, ToastService } from '../../core';
import { Attachment } from '../../models';
import { AttachmentService } from '../../services';
import { BaseComponent } from '../../shared';
import { ImageGarageComponent } from '../image-garage';


/**
 * Component to upload image(s) by drag&drop, file selection and copy&paste.
*/
@Component({
	selector: 'attachment-uploader',
	templateUrl: './attachment-uploader.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class AttachmentUploaderComponent extends BaseComponent {

	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);
	private readonly attachSrv = inject(AttachmentService);

	title = input.required<string>();
	view = input.required<BoneViewEnum>();
	image = input<Attachment | null | undefined>();
	isInvalid = input<boolean>(false);
	isDisabled = input<boolean>(false);
	isLoading = input<boolean>(false);
	removed = output<void>();
	edited = output<void>();
	zoomed = output<void>();

	readonly loaderUrl = `${this.config.cdnUrl}/style/images/loading.gif`;
	readonly validMIMETypes: string[] = this.config.validMIMETypes;
	isImageVisible = true;
	isUploadDisabled = computed(() => this.isDisabled() || this.isLoading());

	// prevent drop event during image choosing from file system (Edge)
	isImageChoosing = signal(false);
	isDropDisabled = computed(() => this.isUploadDisabled() || this.isImageChoosing());

	private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

	/**
	* Drag over event listener.
	*/
	@HostListener('dragover', ['$event']) dragOver(event: DragEvent) {
		if (!this.isDropDisabled()) {
			event.preventDefault();
			event.stopPropagation();
			this.isImageVisible = false;
		}
	}

	/**
	* Drag leave event listener.
	*/
	@HostListener('dragleave', ['$event']) dragLeave(event: DragEvent) {
		if (!this.isDropDisabled()) {
			event.preventDefault();
			event.stopPropagation();
			this.isImageVisible = true;
		}
	}

	/**
	* Drop event listener.
	*/
	@HostListener('drop', ['$event']) drop(event: DragEvent) {
		if (!this.isDropDisabled()) {
			event.preventDefault();
			event.stopPropagation();
			const img = event.dataTransfer?.files?.item(0);
			this.isImageVisible = true;
			this.handleUpload(img!);
		}
	}

	/**
	* Paste event listener.
	*/
	@HostListener('paste', ['$event']) paste(event: ClipboardEvent): void {
		if (!this.isUploadDisabled()) {
			const img = event.clipboardData?.files?.item(0);
			this.handleUpload(img!);
		}
	}

	/**
	* Upload image from file system.
	*/
	chooseFile(event: Event): void {
		this.isImageChoosing.set(false);
		if (!this.isUploadDisabled() && (<HTMLInputElement>event.target).files?.length) {
			const img = (<HTMLInputElement>event.target).files?.item(0);
			this.handleUpload(img!);
		}
		this.fileInput() && (this.fileInput()!.nativeElement.value = '');
	}

	/**
	 * Handle upload checking.
	 */
	private handleUpload(img: File): void {
		const valid = img ? this.checkImageValidation(img) : false;
		valid && this.openImageGarageModal(img, this.view());
	}

	/**
	 * Open modal to handle image categorization.
	 */
	private openImageGarageModal(img: File, view: BoneViewEnum): void {
		const modalRef: NgbModalRef = this.modalSrv.open(ImageGarageComponent, {
			centered: true, backdrop: 'static', windowClass: 'modal-image-garage font-size-bigger', size: 'xl'
		});
		(modalRef.componentInstance as ImageGarageComponent).view.set(view);
		(modalRef.componentInstance as ImageGarageComponent).image.set(this.attachSrv.initUploadImage(img));
	}

	/**
	 * Check image MIME type and size.
	 */
	private checkImageValidation(img: File): boolean {
		let check = true;
		const maxImageSize: number = this.config.maxAttachmentSize;
		const validMIMETypes: string[] = this.config.validMIMETypes || [];
		if (img.size > maxImageSize) {
			this.toastSrv.showWarning(`${this.labels().ATTACHMENT_UPLOADER_COMPONENT_UPLOAD_SIZE_ERROR} (${img.name})`);
			check = false;
		}
		if (!validMIMETypes.includes(img.type)) {
			this.toastSrv.showWarning(`${this.labels().ATTACHMENT_UPLOADER_COMPONENT_UPLOAD_TYPE_ERROR} (${img.name})`);
			check = false;
		}
		return check;
	}
}